import React, { useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";
import WhatsAppFAB from "./WhatsAppFAB";
import AiChatWidget from "./AiChatWidget";

/**
 * Global WhatsApp click interceptor.
 *
 * Without this, anchor tags with `target="_blank"` inside the Emergent
 * preview iframe inherit the iframe's browsing context. `https://wa.me/...`
 * issues a 302 redirect to `api.whatsapp.com/send/...`, which responds with
 * `X-Frame-Options: SAMEORIGIN` / `frame-ancestors 'self'` and gets blocked
 * (ERR_BLOCKED_BY_RESPONSE).
 *
 * Using `window.open(url, '_blank', 'noopener,noreferrer')` from a JS click
 * handler creates a true top-level browsing context that is NOT subject to
 * the parent frame's policy, so the wa.me → api.whatsapp.com redirect opens
 * cleanly. We intercept clicks on any anchor whose href starts with
 * `https://wa.me/` (the only WhatsApp URL format used across the site).
 */
function useWhatsAppClickShim() {
  useEffect(() => {
    const handler = (e) => {
      // Ignore modified clicks (already opens new tab / window naturally)
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
        return;
      }
      // Walk up the DOM to find an anchor element.
      let el = e.target instanceof Element ? e.target : null;
      while (el && el !== document.body) {
        if (el.tagName === "A") {
          const href = el.getAttribute("href") || "";
          if (href.startsWith("https://wa.me/")) {
            e.preventDefault();
            window.open(href, "_blank", "noopener,noreferrer");
            return;
          }
          // Stop walking once we found an anchor (even non-WhatsApp)
          break;
        }
        el = el.parentElement;
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);
}

export default function Layout({ children }) {
  useWhatsAppClickShim();
  return (
    <div className="min-h-screen flex flex-col bg-bah-paper">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <WhatsAppFAB />
      <AiChatWidget />
    </div>
  );
}
